import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Share2, 
  Facebook, 
  Instagram, 
  Twitter, 
  Linkedin,
  Camera,
  Video,
  Image,
  Link,
  TrendingUp,
  Users,
  Heart,
  MessageCircle,
  Share,
  Plus,
  Settings,
  BarChart3,
  Target,
  Zap,
  Award
} from 'lucide-react';

interface ShareableContent {
  id: string;
  type: 'achievement' | 'progress' | 'workout' | 'meal' | 'challenge' | 'streak';
  title: string;
  description: string;
  imageUrl?: string;
  videoUrl?: string;
  hashtags: string[];
  platforms: string[];
  engagement: {
    likes: number;
    shares: number;
    comments: number;
    views: number;
  };
  createdAt: Date;
}

interface SocialMetrics {
  totalShares: number;
  totalEngagement: number;
  platformBreakdown: {
    [platform: string]: {
      shares: number;
      engagement: number;
      reach: number;
    };
  };
  topContent: ShareableContent[];
  engagementRate: number;
  viralCoefficient: number;
}

interface SocialLogin {
  platform: 'facebook' | 'google' | 'apple' | 'twitter' | 'instagram';
  connected: boolean;
  username?: string;
  avatar?: string;
  lastSync?: Date;
}

const SocialMediaPanel: React.FC = () => {
  const [activeTab, setActiveTab] = useState('share');
  const [shareableContent, setShareableContent] = useState<ShareableContent[]>([]);
  const [socialMetrics, setSocialMetrics] = useState<SocialMetrics | null>(null);
  const [socialLogins, setSocialLogins] = useState<SocialLogin[]>([]);
  const [loading, setLoading] = useState(true);
  const [sharing, setSharing] = useState(false);

  // Share form state
  const [shareForm, setShareForm] = useState({
    type: 'achievement',
    title: '',
    description: '',
    imageUrl: '',
    hashtags: '',
    platforms: [] as string[]
  });

  useEffect(() => {
    loadSocialMediaData();
  }, []);

  const loadSocialMediaData = async () => {
    try {
      setLoading(true);
      const [contentData, metricsData, loginsData] = await Promise.all([
        loadShareableContent(),
        loadSocialMetrics(),
        loadSocialLogins()
      ]);

      setShareableContent(contentData);
      setSocialMetrics(metricsData);
      setSocialLogins(loginsData);
    } catch (error) {
      console.error('Error loading social media data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadShareableContent = async (): Promise<ShareableContent[]> => {
    // Mock data - replace with actual API call
    return [
      {
        id: 'content_1',
        type: 'achievement',
        title: '🏆 First Week Warrior',
        description: 'I just earned the "First Week Warrior" achievement! Completed 7 workouts in a week. #FitnessJourney #AchievementUnlocked',
        imageUrl: '/images/achievement-badge.jpg',
        hashtags: ['#FitnessJourney', '#AchievementUnlocked', '#Workout'],
        platforms: ['facebook', 'instagram', 'twitter'],
        engagement: {
          likes: 25,
          shares: 8,
          comments: 12,
          views: 150
        },
        createdAt: new Date()
      },
      {
        id: 'content_2',
        type: 'progress',
        title: '📈 Progress Update',
        description: 'Check out my fitness progress! Lost 5 pounds and gained strength. #Progress #FitnessJourney #Transformation',
        imageUrl: '/images/progress-photo.jpg',
        hashtags: ['#Progress', '#FitnessJourney', '#Transformation'],
        platforms: ['facebook', 'instagram'],
        engagement: {
          likes: 45,
          shares: 15,
          comments: 8,
          views: 300
        },
        createdAt: new Date(Date.now() - 24 * 60 * 60 * 1000)
      }
    ];
  };

  const loadSocialMetrics = async (): Promise<SocialMetrics> => {
    // Mock data - replace with actual API call
    return {
      totalShares: 25,
      totalEngagement: 150,
      platformBreakdown: {
        'facebook': { shares: 10, engagement: 60, reach: 500 },
        'instagram': { shares: 15, engagement: 90, reach: 800 },
        'twitter': { shares: 5, engagement: 20, reach: 200 }
      },
      topContent: [],
      engagementRate: 0.15,
      viralCoefficient: 0.8
    };
  };

  const loadSocialLogins = async (): Promise<SocialLogin[]> => {
    // Mock data - replace with actual API call
    return [
      {
        platform: 'facebook',
        connected: true,
        username: 'John Doe',
        avatar: '/images/avatar.jpg',
        lastSync: new Date()
      },
      {
        platform: 'instagram',
        connected: true,
        username: '@johndoe',
        avatar: '/images/avatar.jpg',
        lastSync: new Date()
      },
      {
        platform: 'twitter',
        connected: false
      },
      {
        platform: 'twitter',
        connected: false
      }
    ];
  };

  const handleShare = async () => {
    try {
      setSharing(true);
      
      const content: ShareableContent = {
        id: `content_${Date.now()}`,
        type: shareForm.type as any,
        title: shareForm.title,
        description: shareForm.description,
        imageUrl: shareForm.imageUrl,
        hashtags: shareForm.hashtags.split(' ').filter(tag => tag.trim()),
        platforms: shareForm.platforms,
        engagement: {
          likes: 0,
          shares: 0,
          comments: 0,
          views: 0
        },
        createdAt: new Date()
      };

      // Share to selected platforms
      await shareToPlatforms(content, shareForm.platforms);
      
      // Add to local content
      setShareableContent(prev => [content, ...prev]);
      
      // Reset form
      setShareForm({
        type: 'achievement',
        title: '',
        description: '',
        imageUrl: '',
        hashtags: '',
        platforms: []
      });

      // Show success message
      alert('Content shared successfully!');
    } catch (error) {
      console.error('Error sharing content:', error);
      alert('Error sharing content. Please try again.');
    } finally {
      setSharing(false);
    }
  };

  const shareToPlatforms = async (content: ShareableContent, platforms: string[]) => {
    // Implementation would share to actual platforms
    console.log('Sharing to platforms:', platforms, content);
  };

  const connectSocialAccount = async (platform: string) => {
    try {
      // Implementation would connect to social account
      console.log('Connecting to:', platform);
      alert(`Connecting to ${platform}...`);
    } catch (error) {
      console.error('Error connecting to social account:', error);
      alert('Error connecting to social account. Please try again.');
    }
  };

  const disconnectSocialAccount = async (platform: string) => {
    try {
      // Implementation would disconnect social account
      console.log('Disconnecting from:', platform);
      alert(`Disconnected from ${platform}`);
    } catch (error) {
      console.error('Error disconnecting from social account:', error);
      alert('Error disconnecting from social account. Please try again.');
    }
  };

  const getPlatformIcon = (platform: string) => {
    switch (platform) {
      case 'facebook': return <Facebook className="w-4 h-4" />;
      case 'instagram': return <Instagram className="w-4 h-4" />;
      case 'twitter': return <Twitter className="w-4 h-4" />;
      case 'linkedin': return <Linkedin className="w-4 h-4" />;
      default: return <Share2 className="w-4 h-4" />;
    }
  };

  const getPlatformColor = (platform: string): string => {
    switch (platform) {
      case 'facebook': return '';
      case 'instagram': return 'bg-pink-600 hover:bg-pink-700';
      case 'twitter': return '';
      case 'linkedin': return '';
      default: return 'hover:opacity-80';
    }
  };

  const getPlatformStyle = (platform: string) => {
    switch (platform) {
      case 'facebook': return { background: 'var(--gradient-cta-primary)', color: 'white', boxShadow: 'var(--shadow-lg)' };
      case 'instagram': return { background: '#E1306C', color: 'white', boxShadow: 'var(--shadow-lg)' };
      case 'twitter': return { background: 'var(--gradient-cta-primary)', color: 'white', boxShadow: 'var(--shadow-lg)' };
      case 'linkedin': return { background: 'var(--gradient-cta-primary)', color: 'white', boxShadow: 'var(--shadow-lg)' };
      default: return { background: 'var(--color-background-secondary)', color: 'var(--color-text)' };
    }
  };

  const getContentTypeIcon = (type: string) => {
    switch (type) {
      case 'achievement': return <Award className="w-4 h-4" />;
      case 'progress': return <TrendingUp className="w-4 h-4" />;
      case 'workout': return <Zap className="w-4 h-4" />;
      case 'meal': return <Target className="w-4 h-4" />;
      case 'challenge': return <Users className="w-4 h-4" />;
      case 'streak': return <TrendingUp className="w-4 h-4" />;
      default: return <Share2 className="w-4 h-4" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-end">
        <div className="text-right">
          <div className="text-3xl font-bold text-blue-600">{socialMetrics?.totalShares || 0}</div>
          <div className="text-sm" style={{ color: 'var(--color-text-muted)', textShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>Total Shares</div>
        </div>
      </div>

      {/* Social Metrics */}
      {socialMetrics && (
        <div className="sculptd-card-elevated sculptd-sheen sculptd-container-padding" style={{
          boxShadow: 'var(--shadow-card-separation)'
        }}>
          <div className="mb-6">
            <h3 className="flex items-center space-x-3 text-xl font-semibold" style={{ 
              color: 'var(--color-text)',
              textShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}>
              <BarChart3 className="w-6 h-6" style={{ color: 'var(--color-primary)' }} />
              <span>Social Metrics</span>
            </h3>
          </div>
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">{socialMetrics.totalShares}</div>
                <div className="text-sm" style={{ color: 'var(--color-text-muted)', textShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>Total Shares</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">{socialMetrics.totalEngagement}</div>
                <div className="text-sm" style={{ color: 'var(--color-text-muted)', textShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>Total Engagement</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600">{(socialMetrics.engagementRate * 100).toFixed(1)}%</div>
                <div className="text-sm" style={{ color: 'var(--color-text-muted)', textShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>Engagement Rate</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-orange-600">{socialMetrics.viralCoefficient.toFixed(1)}</div>
                <div className="text-sm" style={{ color: 'var(--color-text-muted)', textShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>Viral Coefficient</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="share">Share Content</TabsTrigger>
          <TabsTrigger value="content">My Content</TabsTrigger>
          <TabsTrigger value="connections">Connections</TabsTrigger>
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
        </TabsList>

        {/* Share Content Tab */}
        <TabsContent value="share" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Create Shareable Content</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text)', textShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                    Content Type
                  </label>
                  <select
                    value={shareForm.type}
                    onChange={(e) => setShareForm(prev => ({ ...prev, type: e.target.value }))}
                    className="w-full p-2 border rounded-md focus:ring-2 focus:border-transparent"
                    style={{ 
                      borderColor: 'var(--color-border)', 
                      background: 'white',
                      color: 'var(--color-text)'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = 'var(--color-primary)';
                      e.target.style.boxShadow = '0 0 0 2px rgba(58, 175, 169, 0.2)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = 'var(--color-border)';
                      e.target.style.boxShadow = 'none';
                    }}
                  >
                    <option value="achievement">Achievement</option>
                    <option value="progress">Progress</option>
                    <option value="workout">Workout</option>
                    <option value="meal">Meal</option>
                    <option value="challenge">Challenge</option>
                    <option value="streak">Streak</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text)', textShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                    Title
                  </label>
                  <Input
                    value={shareForm.title}
                    onChange={(e) => setShareForm(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Enter title"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text)', textShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                  Description
                </label>
                <textarea
                  value={shareForm.description}
                  onChange={(e) => setShareForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Enter description"
                  rows={3}
                  className="w-full p-3 border rounded-md focus:ring-2 focus:border-transparent resize-none"
                  style={{ 
                    borderColor: 'var(--color-charcoal)',
                    backgroundColor: 'var(--color-white)',
                    color: 'var(--color-text)'
                  }}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text)', textShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                  Image URL (optional)
                </label>
                <Input
                  value={shareForm.imageUrl}
                  onChange={(e) => setShareForm(prev => ({ ...prev, imageUrl: e.target.value }))}
                  placeholder="Enter image URL"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text)', textShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                  Hashtags
                </label>
                <Input
                  value={shareForm.hashtags}
                  onChange={(e) => setShareForm(prev => ({ ...prev, hashtags: e.target.value }))}
                  placeholder="#fitness #workout #motivation"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text)', textShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                  Platforms
                </label>
                <div className="flex flex-wrap gap-2">
                  {['facebook', 'instagram', 'twitter', 'linkedin'].map((platform) => (
                    <button
                      key={platform}
                      onClick={() => {
                        const platforms = shareForm.platforms.includes(platform)
                          ? shareForm.platforms.filter(p => p !== platform)
                          : [...shareForm.platforms, platform];
                        setShareForm(prev => ({ ...prev, platforms }));
                      }}
                      className={`flex items-center space-x-1 px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                        shareForm.platforms.includes(platform)
                          ? getPlatformColor(platform)
                          : 'hover:opacity-80'
                      } text-white`}
                      style={{ 
                        background: shareForm.platforms.includes(platform) 
                          ? getPlatformStyle(platform).background
                          : 'var(--color-background-secondary)',
                        color: shareForm.platforms.includes(platform)
                          ? 'white'
                          : 'var(--color-text)'
                      }}
                    >
                      {getPlatformIcon(platform)}
                      <span className="capitalize">{platform}</span>
                    </button>
                  ))}
                </div>
              </div>

              <Button 
                onClick={handleShare} 
                disabled={sharing || !shareForm.title || !shareForm.description}
                className="w-full"
              >
                {sharing ? 'Sharing...' : 'Share Content'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* My Content Tab */}
        <TabsContent value="content" className="space-y-4">
          <div className="space-y-4">
            {shareableContent.map((content) => (
              <Card key={content.id} className="hover:shadow-md transition-shadow">
                <CardContent className="sculptd-container-padding">
                  <div className="flex items-start space-x-4">
                    {content.imageUrl && (
                      <img
                        src={content.imageUrl}
                        alt={content.title}
                        className="w-16 h-16 rounded-lg object-cover"
                      />
                    )}
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-1">
                        {getContentTypeIcon(content.type)}
                        <h4 className="font-semibold">{content.title}</h4>
                        <span className="px-2 py-1 rounded-full text-xs font-medium capitalize" style={{ 
                          background: 'var(--color-secondary)', 
                          color: 'var(--color-white)' 
                        }}>
                          {content.type}
                        </span>
                      </div>
                      <p className="text-sm mb-2" style={{ color: 'var(--color-text-muted)' }}>{content.description}</p>
                      <div className="flex items-center space-x-4 text-xs" style={{ color: 'var(--color-text-muted)' }}>
                        <div className="flex items-center space-x-1">
                          <Heart className="w-3 h-3" />
                          <span>{content.engagement.likes}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <MessageCircle className="w-3 h-3" />
                          <span>{content.engagement.comments}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Share className="w-3 h-3" />
                          <span>{content.engagement.shares}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <TrendingUp className="w-3 h-3" />
                          <span>{content.engagement.views} views</span>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3 mt-2">
                        {content.platforms.map((platform) => (
                          <div key={platform} className="flex items-center space-x-1">
                            {getPlatformIcon(platform)}
                            <span className="text-xs capitalize">{platform}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Connections Tab */}
        <TabsContent value="connections" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Social Media Connections</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {socialLogins.map((login) => (
                  <div key={login.platform} className="flex items-center justify-between sculptd-container-padding border rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className="p-2 rounded-full" style={{ background: 'var(--color-background-secondary)' }}>
                        {getPlatformIcon(login.platform)}
                      </div>
                      <div>
                        <h4 className="font-semibold capitalize">{login.platform}</h4>
                        {login.connected ? (
                          <div>
                            <p className="text-sm text-gray-600">{login.username}</p>
                            <p className="text-xs text-gray-500">
                              Last sync: {login.lastSync?.toLocaleDateString()}
                            </p>
                          </div>
                        ) : (
                          <p className="text-sm text-gray-500">Not connected</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      {login.connected ? (
                        <>
                          <span className="px-2 py-1 rounded-full text-xs font-medium" style={{ 
                            background: 'var(--gradient-cta-primary)', 
                            color: 'var(--color-white)',
                            boxShadow: 'var(--shadow-lg)'
                          }}>Connected</span>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => disconnectSocialAccount(login.platform)}
                          >
                            Disconnect
                          </Button>
                        </>
                      ) : (
                        <Button
                          size="sm"
                          onClick={() => connectSocialAccount(login.platform)}
                          className={getPlatformColor(login.platform)}
                        >
                          Connect
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Analytics Tab */}
        <TabsContent value="analytics" className="space-y-4">
          {socialMetrics && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Platform Breakdown</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {Object.entries(socialMetrics.platformBreakdown).map(([platform, data]) => (
                      <div key={platform} className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          {getPlatformIcon(platform)}
                          <span className="capitalize font-medium">{platform}</span>
                        </div>
                        <div className="text-right">
                          <div className="text-sm font-semibold">{data.shares} shares</div>
                          <div className="text-xs text-gray-500">{data.engagement} engagement</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle>Engagement Insights</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Engagement Rate</span>
                      <span className="text-sm font-semibold">{(socialMetrics.engagementRate * 100).toFixed(1)}%</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Viral Coefficient</span>
                      <span className="text-sm font-semibold">{socialMetrics.viralCoefficient.toFixed(1)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Total Reach</span>
                      <span className="text-sm font-semibold">
                        {Object.values(socialMetrics.platformBreakdown).reduce((sum, data) => sum + data.reach, 0)}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SocialMediaPanel;
